import { VerificationStatus } from "shared/types";
import { has } from "shared/utils";
import { Cell } from "views/applications-id/components/ApplicantsTable/components/Cell";
import {
  ApplicantOrInvitee,
  Verification
} from "views/applications-id/components/ApplicantsTable/types";
import {
  calculatePastDueAccounts,
  summaryMoneyToDisplay
} from "views/applications-id/components/ApplicantsTable/utils";
import { useAuthContext } from "providers/Auth";
import { CanadianCreditReportStatus } from "../CanadianCreditReportStatus";
import { DataWithAuditFlag } from "./components/DataWithAuditFlag";

// Expanded Canadian credit report mirrors the US credit report layout: five
// data cells reading the exact same VerificationSummary fields (score, past due
// accounts, balance past due, balance of collections, bankruptcy). We omit the
// US-specific criteria `check` marks here and just render the values, gated on
// the caCreditReport permission.
const CanadianCreditReportExpandedSection = ({
  applicant,
  verification,
  hasAuditFlagEnabled
}: {
  applicant: ApplicantOrInvitee;
  verification: Verification;
  hasAuditFlagEnabled: boolean;
}) => {
  const { permissions } = useAuthContext();
  const isPermitted = permissions.canAccessReports.caCreditReport;

  const auditSummaryFlags =
    "audit_summary_flags" in applicant
      ? applicant.audit_summary_flags
      : undefined;

  return (
    <>
      <Cell
        isFirst
        isDataCell
        className="relative"
        isPermitted={isPermitted}
        showRestrictedFlag={!isPermitted}
      >
        <DataWithAuditFlag
          hasData={has(verification.summary?.score)}
          dataValue={verification.summary?.score}
          hasAuditFlagEnabled={hasAuditFlagEnabled}
          auditSummaryFlags={auditSummaryFlags}
        />
      </Cell>
      <Cell isDataCell isPermitted={isPermitted}>
        <DataWithAuditFlag
          hasData={has(verification.summary)}
          dataValue={calculatePastDueAccounts(verification.summary)}
          hasAuditFlagEnabled={hasAuditFlagEnabled}
          auditSummaryFlags={auditSummaryFlags}
        />
      </Cell>
      <Cell isDataCell isPermitted={isPermitted}>
        <DataWithAuditFlag
          hasData={
            has(verification.summary?.balance_past_due) &&
            verification.summary.balance_past_due !== "No data"
          }
          dataValue={summaryMoneyToDisplay(
            verification.summary?.balance_past_due
          )}
          hasAuditFlagEnabled={hasAuditFlagEnabled}
          auditSummaryFlags={auditSummaryFlags}
        />
      </Cell>
      <Cell isDataCell isPermitted={isPermitted}>
        <DataWithAuditFlag
          hasData={
            has(verification.summary?.balance_of_collections) &&
            verification.summary.balance_of_collections !== "No data"
          }
          dataValue={summaryMoneyToDisplay(
            verification.summary?.balance_of_collections
          )}
          hasAuditFlagEnabled={hasAuditFlagEnabled}
          auditSummaryFlags={auditSummaryFlags}
        />
      </Cell>
      <Cell isDataCell isPermitted={isPermitted}>
        <DataWithAuditFlag
          hasData={has(verification.summary?.bankruptcy)}
          dataValue={verification.summary?.bankruptcy}
          hasAuditFlagEnabled={hasAuditFlagEnabled}
          auditSummaryFlags={auditSummaryFlags}
        />
      </Cell>
    </>
  );
};

export const CanadianCreditReportSection = ({
  applicant,
  verification,
  expanded,
  hasAuditFlagEnabled
}: {
  applicant: ApplicantOrInvitee;
  verification: Verification;
  expanded?: boolean;
  hasAuditFlagEnabled: boolean;
}) => {
  const { permissions } = useAuthContext();

  if (expanded)
    return (
      <CanadianCreditReportExpandedSection
        applicant={applicant}
        verification={verification}
        hasAuditFlagEnabled={hasAuditFlagEnabled}
      />
    );

  return (
    <Cell
      isDataCell
      className="relative"
      isPermitted={permissions.canAccessReports.caCreditReport}
      showRestrictedFlag={!permissions.canAccessReports.caCreditReport}
    >
      {has(verification.status) ? (
        <CanadianCreditReportStatus
          status={verification.status as VerificationStatus}
          score={verification.summary?.score}
        />
      ) : (
        "-"
      )}
    </Cell>
  );
};
